import { Injectable, inject, signal } from '@angular/core';
import { WorkspaceFacade } from '../workspace';
import {
  TicketsExecutionRuntime,
  readPersistedExecutionRunId,
  persistExecutionRunId,
  clearPersistedExecutionRunId,
} from './tickets.models';

const initialExecution: TicketsExecutionRuntime = {
  active: false,
  mode: 'ticket',
  ticketId: '',
  ticketTitle: '',
  repoFullName: '',
  model: '',
  phase: 'idle',
  startedAt: null,
  lastUpdateAt: null,
  liveFollowConnected: false,
};

/**
 * Transient live execution runtime for ticket runs. NOT persisted (except a
 * localStorage run-id pointer for resume-after-reload). Kept as a focused signal
 * service rather than NgRx because it is live, fast-changing run state driven by
 * the exec/automation services — not shared persisted app state.
 */
@Injectable({ providedIn: 'root' })
export class TicketsExecutionStore {
  private workspace = inject(WorkspaceFacade);
  private _execution = signal<TicketsExecutionRuntime>(initialExecution);
  readonly execution = this._execution.asReadonly();

  private resolvePid(override?: string | null): string | null {
    const o = String(override ?? '').trim();
    return o || this.workspace.activeProjectId();
  }

  setExecutionRuntime(next: Partial<TicketsExecutionRuntime> & { projectId?: string | null }): void {
    const { projectId, ...patch } = next as any;
    const merged: TicketsExecutionRuntime = { ...this._execution(), ...patch, lastUpdateAt: Date.now() };
    const runId = String(merged.runId ?? '').trim();
    if (runId) persistExecutionRunId(runId, this.resolvePid(projectId));
    this._execution.set(merged);
  }

  resetExecutionRuntime(): void {
    this._execution.set({ ...initialExecution, lastUpdateAt: Date.now() });
  }

  getPersistedExecutionRunId(projectIdOverride?: string | null): string | null {
    return readPersistedExecutionRunId(this.resolvePid(projectIdOverride));
  }

  clearPersistedExecutionRunId(projectIdOverride?: string | null): void {
    clearPersistedExecutionRunId(this.resolvePid(projectIdOverride));
  }
}
